import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import MediaSelector from '../MediaSelector';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';
import { normalizeUrl } from '../../../utils/format';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Filter,
  Image,
  Palette,
  ShoppingCart,
  Star,
  X,
  FolderOpen,
  Tag as TagIcon,
  CheckCircle,
  Upload,
  Globe,
  Camera,
  Loader2
} from 'lucide-react';

const AdminEcommerceDesigns = () => {
  const [designs, setDesigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    long_description: '',
    slug: '',
    category_ids: [],
    image_url: '',
    demo_url: '',
    admin_url: '',
    purchase_url: '',
    price: '',
    tags: [],
    features: [],
    is_active: true,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [tagFormData, setTagFormData] = useState({
    name: '',
    description: '',
    color: '#EC4899',
    sort_order: 0,
    is_active: true
  });
  const [capturingScreenshots, setCapturingScreenshots] = useState(new Set());
  const [bulkScreenshotProgress, setBulkScreenshotProgress] = useState(null);

  useEffect(() => {
    fetchDesigns();
    fetchCategories();
    fetchTags();
  }, []);

  const fetchMediaFiles = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.MEDIA);
      const data = await response.json();
      
      if (data.status === 'success') {
        setMediaFiles(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching media files:', error);
    }
  };

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      const response = await adminApiFetch(API_URLS.ADMIN.ECOMMERCE_DESIGNS);
      const data = await response.json();
      
      if (data.status === 'success') {
        setDesigns(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching designs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.ECOMMERCE_DESIGN_CATEGORIES);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.ECOMMERCE_DESIGN_TAGS);
      const result = await response.json();
      
      if (result.status === 'success') {
        setTags(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleCreateTag = () => {
    setEditingTag(null);
    setTagFormData({
      name: '',
      description: '',
      color: '#EC4899',
      sort_order: 0,
      is_active: true
    });
    setShowTagModal(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagFormData({
      name: tag.name || '',
      description: tag.description || '',
      color: tag.color || '#EC4899',
      sort_order: tag.sort_order || 0,
      is_active: tag.is_active !== false
    });
    setShowTagModal(true);
  };

  const handleSaveTag = async () => {
    try {
      const url = editingTag 
        ? `${API_URLS.ADMIN.UPDATE_ECOMMERCE_DESIGN_TAG}&id=${editingTag.id}`
        : API_URLS.ADMIN.CREATE_ECOMMERCE_DESIGN_TAG;
      
      const response = await adminApiFetch(url, {
        method: 'POST',
        body: JSON.stringify(tagFormData)
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        alert(editingTag ? 'Tag güncellendi' : 'Tag oluşturuldu');
        setShowTagModal(false);
        fetchTags();
      } else {
        alert(result.message || 'Tag kaydedilemedi');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Bu tag\'i silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.DELETE_ECOMMERCE_DESIGN_TAG}&id=${tagId}`, {
        method: 'POST'
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        alert('Tag silindi');
        fetchTags();
        setSelectedTags(selectedTags.filter(id => id !== tagId));
      } else {
        alert(result.message || 'Tag silinemedi');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const handleBulkDeleteTags = async () => {
    if (selectedTags.length === 0) {
      alert('Lütfen silmek için en az bir tag seçin');
      return;
    }

    if (!window.confirm(`${selectedTags.length} tag'i silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const tagId of selectedTags) {
        try {
          const response = await adminApiFetch(`${API_URLS.ADMIN.DELETE_ECOMMERCE_DESIGN_TAG}&id=${tagId}`, {
            method: 'POST'
          });
          const result = await response.json();
          if (result.status === 'success') {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      if (failCount > 0) {
        alert(`${successCount} tag silindi, ${failCount} tag silinemedi`);
      } else {
        alert(`${successCount} tag başarıyla silindi`);
      }

      setSelectedTags([]);
      fetchTags();
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const handleToggleTagStatus = async (tagId) => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.TOGGLE_ECOMMERCE_DESIGN_TAG_STATUS, {
        method: 'POST',
        body: JSON.stringify({ id: tagId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        fetchTags();
      } else {
        alert(result.message || 'Durum güncellenemedi');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const handleToggleTag = (tagName) => {
    setFormData(prev => {
      const currentTags = prev.tags || [];
      const hasTag = currentTags.includes(tagName);
      
      return {
        ...prev,
        tags: hasTag 
          ? currentTags.filter(t => t !== tagName)
          : [...currentTags, tagName]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingDesign 
        ? `${API_URLS.ADMIN.ADMIN_API}?action=updateEcommerceDesign&id=${editingDesign.id}`
        : `${API_URLS.ADMIN.ADMIN_API}?action=createEcommerceDesign`;
      
      const response = await adminApiFetch(url, {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      // Get response text first
      const responseText = await response.text();
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was:', responseText);
        alert('Sunucudan geçersiz yanıt alındı. Lütfen tekrar deneyin.');
        return;
      }
      
      if (result.status === 'success') {
        alert(editingDesign ? 'Tasarım güncellendi' : 'Tasarım oluşturuldu');
        fetchDesigns();
        setIsModalOpen(false);
        setEditingDesign(null);
        setFormData({
          name: '',
          description: '',
          long_description: '',
          slug: '',
          category_ids: [],
          image_url: '',
          demo_url: '',
          admin_url: '',
          purchase_url: '',
          price: '',
          tags: [],
          features: [],
          is_active: true
        });
        setTagInput('');
        setFeatureInput('');
      } else {
        alert(result.message || 'Bir hata oluştu');
      }
    } catch (error) {
      console.error('Error saving design:', error);
      alert('Tasarım kaydedilirken bir hata oluştu: ' + error.message);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAddFeature = () => {
    const feature = featureInput.trim();
    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
    if (feature && !currentFeatures.includes(feature)) {
      setFormData({
        ...formData,
        features: [...currentFeatures, feature]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
    setFormData({
      ...formData,
      features: currentFeatures.filter(feature => feature !== featureToRemove)
    });
  };

  const handleFeatureInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const handleEdit = (design) => {
    setEditingDesign(design);
    
    // Parse tags safely
    let parsedTags = [];
    if (design.tags) {
      if (typeof design.tags === 'string') {
        try {
          const parsed = JSON.parse(design.tags);
          parsedTags = Array.isArray(parsed) ? parsed : [];
        } catch {
          // If JSON parse fails, try splitting by comma (legacy format)
          parsedTags = design.tags.split(',').map(t => t.trim()).filter(t => t);
        }
      } else if (Array.isArray(design.tags)) {
        parsedTags = design.tags.filter(t => t); // Remove empty values
      }
    }
    // Ensure it's always an array and normalize tag names (trim whitespace)
    if (!Array.isArray(parsedTags)) {
      parsedTags = [];
    }
    // Normalize tag names (trim and filter empty)
    parsedTags = parsedTags.map(t => typeof t === 'string' ? t.trim() : String(t).trim()).filter(t => t);
    
    // Parse features safely
    let parsedFeatures = [];
    if (design.features) {
      if (typeof design.features === 'string') {
        try {
          const parsed = JSON.parse(design.features);
          parsedFeatures = Array.isArray(parsed) ? parsed : [];
        } catch {
          parsedFeatures = [];
        }
      } else if (Array.isArray(design.features)) {
        parsedFeatures = design.features;
      }
    }
    // Ensure it's always an array
    if (!Array.isArray(parsedFeatures)) {
      parsedFeatures = [];
    }
    
    // Parse category_ids (can be array or single value for backward compatibility)
    let parsedCategoryIds = [];
    if (design.category_ids && Array.isArray(design.category_ids)) {
      parsedCategoryIds = design.category_ids.filter(id => id); // Remove empty values
    } else if (design.category_id) {
      // Backward compatibility: single category_id
      parsedCategoryIds = [design.category_id];
    }
    
    setFormData({
      name: design.name || '',
      description: design.description || '',
      long_description: design.long_description || '',
      slug: design.slug || '',
      category_ids: parsedCategoryIds,
      image_url: design.image_url || '',
      demo_url: design.demo_url || '',
      admin_url: design.admin_url || '',
      purchase_url: design.purchase_url || '',
      price: design.price || '',
      tags: parsedTags,
      features: parsedFeatures,
      is_active: design.is_active !== false,
      meta_title: design.meta_title || '',
      meta_description: design.meta_description || '',
      meta_keywords: design.meta_keywords || '',
      og_image: design.og_image || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu tasarımı silmek istediğinizden emin misiniz?')) {
      try {
        const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteEcommerceDesign&id=${id}`, {
          method: 'POST',
        });

        // Get response text first to check if it's valid JSON
        const responseText = await response.text();
        
        if (!responseText || responseText.trim() === '') {
          // Empty response, assume success
          fetchDesigns();
          setSelectedDesigns(selectedDesigns.filter(designId => designId !== id));
          return;
        }

        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON Parse Error:', parseError);
          console.error('Response was:', responseText);
          alert('Sunucudan geçersiz yanıt alındı. Tasarım silinmiş olabilir.');
          fetchDesigns(); // Refresh list anyway
          return;
        }
        
        if (result.status === 'success') {
          fetchDesigns();
          setSelectedDesigns(selectedDesigns.filter(designId => designId !== id));
        } else {
          alert(result.message || 'Tasarım silinirken bir hata oluştu');
        }
      } catch (error) {
        console.error('Error deleting design:', error);
        alert('Tasarım silinirken bir hata oluştu: ' + error.message);
      }
    }
  };

  const handleBulkDeleteDesigns = async () => {
    if (selectedDesigns.length === 0) {
      alert('Lütfen silmek için en az bir tasarım seçin');
      return;
    }

    if (!window.confirm(`${selectedDesigns.length} tasarımı silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const designId of selectedDesigns) {
        try {
          const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteEcommerceDesign&id=${designId}`, {
            method: 'POST',
          });
          const responseText = await response.text();
          
          if (!responseText || responseText.trim() === '') {
            successCount++;
          } else {
            try {
              const result = JSON.parse(responseText);
              if (result.status === 'success') {
                successCount++;
              } else {
                failCount++;
              }
            } catch {
              successCount++;
            }
          }
        } catch (error) {
          failCount++;
        }
      }

      if (failCount > 0) {
        alert(`${successCount} tasarım silindi, ${failCount} tasarım silinemedi`);
      } else {
        alert(`${successCount} tasarım başarıyla silindi`);
      }

      setSelectedDesigns([]);
      fetchDesigns();
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert('Lütfen .xlsx veya .xls formatında bir dosya seçin');
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=importEcommerceDesigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.status === 'success') {
        alert(`Import başarılı! ${result.inserted || 0} tasarım eklendi, ${result.updated || 0} tasarım güncellendi.`);
        fetchDesigns();
      } else {
        alert(result.message || 'Import sırasında bir hata oluştu');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import sırasında bir hata oluştu: ' + error.message);
    } finally {
      setIsImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ECOMMERCE_DESIGNS}?action=toggleEcommerceDesignStatus&id=${id}`, {
        method: 'PATCH',
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        fetchDesigns();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleMediaSelect = (mediaUrl) => {
    setFormData({ ...formData, image_url: mediaUrl });
    setIsMediaModalOpen(false);
  };

  const openMediaLibrary = () => {
    fetchMediaFiles();
    setIsMediaModalOpen(true);
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = Number(String(value).replace(/[^0-9.,-]/g, '').replace(',', '.'));
    if (Number.isNaN(normalized)) {
      return String(value);
    }
    return `₺${normalized.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const captureScreenshot = async (design, force = false) => {
    const url = design.demo_url || design.admin_url;
    if (!url) {
      alert('Bu tasarım için demo URL veya admin URL bulunamadı');
      return;
    }

    const normalizedUrl = normalizeUrl(url);
    setCapturingScreenshots(prev => new Set(prev).add(design.id));
    
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          design_id: design.id,
          design_type: 'ecommerce',
          url: normalizedUrl,
          force: force
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        // Update design in local state
        setDesigns(prevDesigns => 
          prevDesigns.map(d => 
            d.id === design.id 
              ? { ...d, image_url: result.data.image_url }
              : d
          )
        );
        alert('Ekran görüntüsü başarıyla alındı ve kaydedildi!');
      } else {
        alert('Ekran görüntüsü alınamadı: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Screenshot capture error:', error);
      alert('Ekran görüntüsü alınırken bir hata oluştu: ' + error.message);
    } finally {
      setCapturingScreenshots(prev => {
        const newSet = new Set(prev);
        newSet.delete(design.id);
        return newSet;
      });
    }
  };

  const captureBulkScreenshots = async () => {
    const designsToCapture = filteredDesigns.filter(design => {
      const url = design.demo_url || design.admin_url;
      return url && (!design.image_url || true); // Capture all, even if image exists
    });

    if (designsToCapture.length === 0) {
      alert('Ekran görüntüsü alınabilecek tasarım bulunamadı (demo URL veya admin URL gerekli)');
      return;
    }

    if (!confirm(`${designsToCapture.length} tasarım için ekran görüntüsü alınacak. Devam etmek istiyor musunuz?`)) {
      return;
    }

    setBulkScreenshotProgress({
      total: designsToCapture.length,
      current: 0,
      completed: 0,
      failed: 0
    });

    for (let i = 0; i < designsToCapture.length; i++) {
      const design = designsToCapture[i];
      setBulkScreenshotProgress(prev => ({
        ...prev,
        current: i + 1
      }));

      try {
        const url = design.demo_url || design.admin_url;
        const normalizedUrl = normalizeUrl(url);
        const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            design_id: design.id,
            design_type: 'ecommerce',
            url: normalizedUrl,
            force: false
          })
        });

        const result = await response.json();
        
        if (result.status === 'success') {
          setDesigns(prevDesigns => 
            prevDesigns.map(d => 
              d.id === design.id 
                ? { ...d, image_url: result.data.image_url }
                : d
            )
          );
          setBulkScreenshotProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
        } else {
          setBulkScreenshotProgress(prev => ({
            ...prev,
            failed: prev.failed + 1
          }));
        }
      } catch (error) {
        console.error(`Screenshot capture error for design ${design.id}:`, error);
        setBulkScreenshotProgress(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }

      // Small delay to avoid overwhelming the server
      if (i < designsToCapture.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setBulkScreenshotProgress(prev => {
      alert(`Toplu ekran görüntüsü alma tamamlandı!\nBaşarılı: ${prev.completed}\nBaşarısız: ${prev.failed}`);
      return null;
    });
  };

  const filteredDesigns = designs.filter(design => {
    const matchesSearch = design.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         design.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Support multiple categories - check if any category matches
    let matchesCategory = false;
    if (selectedCategory === 'all') {
      matchesCategory = true;
    } else {
      // Support new format: category_names array
      if (design.category_names && Array.isArray(design.category_names) && design.category_names.length > 0) {
        matchesCategory = design.category_names.includes(selectedCategory);
      }
      // Support new format: category_ids array
      else if (design.category_ids && Array.isArray(design.category_ids) && design.category_ids.length > 0) {
        const cat = categories.find(c => design.category_ids.includes(c.id) || c.name === selectedCategory);
        matchesCategory = !!cat;
      }
      // Support old format: single category
      else {
        matchesCategory = design.category === selectedCategory || design.category_id === selectedCategory;
      }
    }
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-Ticaret Tasarımları</h1>
          <p className="text-gray-600">E-ticaret tasarımlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => document.getElementById('excel-file-input')?.click()} 
            className="bg-green-600 hover:bg-green-700"
            disabled={isImporting}
          >
            <Upload className="w-4 h-4 mr-2" />
            {isImporting ? 'Import Ediliyor...' : 'Excel Import'}
          </Button>
          <input
            id="excel-file-input"
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleExcelImport}
          />
          <Button onClick={() => setIsModalOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Tasarım
          </Button>
        </div>
      </div>

      {/* Tag Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <TagIcon className="w-5 h-5 text-purple-600" />
              <CardTitle>Tag Yönetimi</CardTitle>
              {selectedTags.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedTags.length} seçili
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              {selectedTags.length > 0 && (
                <Button 
                  onClick={handleBulkDeleteTags} 
                  size="sm"
                  variant="destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Seçilenleri Sil ({selectedTags.length})
                </Button>
              )}
              <Button onClick={handleCreateTag} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Tag
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tags.length > 0 && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTags.length === tags.length && tags.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTags(tags.map(t => t.id));
                    } else {
                      setSelectedTags([]);
                    }
                  }}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span>Tümünü Seç</span>
                {selectedTags.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedTags.length} seçili
                  </Badge>
                )}
              </label>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {tags.length === 0 ? (
              <p className="text-sm text-gray-500">Henüz tag eklenmemiş</p>
            ) : (
              tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-shadow"
                  style={{ borderLeftColor: tag.color, borderLeftWidth: '3px' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTags([...selectedTags, tag.id]);
                      } else {
                        setSelectedTags(selectedTags.filter(id => id !== tag.id));
                      }
                    }}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <Badge style={{ backgroundColor: tag.color }} className="text-white">
                    {tag.name}
                  </Badge>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTag(tag)}
                      className="h-6 w-6 p-0"
                      title="Düzenle"
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleTagStatus(tag.id)}
                      className="h-6 w-6 p-0"
                      title={tag.is_active ? "Pasif Yap" : "Aktif Yap"}
                    >
                      {tag.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTag(tag.id)}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      title="Sil"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Tasarım ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="sm:w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">Tüm Kategoriler</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {filteredDesigns.length > 0 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedDesigns.length === filteredDesigns.length && filteredDesigns.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDesigns(filteredDesigns.map(d => d.id));
                        } else {
                          setSelectedDesigns([]);
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span>Tümünü Seç</span>
                  </label>
                  {selectedDesigns.length > 0 && (
                    <Badge variant="secondary">
                      {selectedDesigns.length} tasarım seçili
                    </Badge>
                  )}
                </div>
                {selectedDesigns.length > 0 && (
                  <Button 
                    onClick={handleBulkDeleteDesigns} 
                    size="sm"
                    variant="destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Seçilenleri Sil ({selectedDesigns.length})
                  </Button>
                )}
                <Button 
                  onClick={captureBulkScreenshots} 
                  size="sm"
                  variant="outline"
                  disabled={bulkScreenshotProgress !== null}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                >
                  {bulkScreenshotProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      İşleniyor ({bulkScreenshotProgress.current}/{bulkScreenshotProgress.total})
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Toplu Ekran Görüntüsü Al
                    </>
                  )}
                </Button>
              </div>
            )}
            {bulkScreenshotProgress && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-700">İlerleme:</span>
                  <span className="text-sm font-medium text-blue-700">
                    {bulkScreenshotProgress.completed} başarılı, {bulkScreenshotProgress.failed} başarısız
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkScreenshotProgress.current / bulkScreenshotProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Designs Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-2 text-gray-600">Tasarımlar yükleniyor...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDesigns.map((design) => (
            <Card key={design.id} className="overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                {design.image_url ? (
                  <img
                    src={design.image_url}
                    alt={design.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Palette className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 left-2">
                  <input
                    type="checkbox"
                    checked={selectedDesigns.includes(design.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDesigns([...selectedDesigns, design.id]);
                      } else {
                        setSelectedDesigns(selectedDesigns.filter(id => id !== design.id));
                      }
                    }}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 bg-white border-2 border-gray-300"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="absolute top-2 right-2">
                  <Badge variant={design.is_active ? "default" : "secondary"}>
                    {design.is_active ? "Aktif" : "Pasif"}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{design.name}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{design.description}</p>
                {/* Show first category if available */}
                {(() => {
                  let categoryName = null;
                  // Support new format: category_names array
                  if (design.category_names && Array.isArray(design.category_names) && design.category_names.length > 0) {
                    categoryName = design.category_names[0];
                  }
                  // Support new format: category_ids array - find category by ID
                  else if (design.category_ids && Array.isArray(design.category_ids) && design.category_ids.length > 0) {
                    const cat = categories.find(c => c.id === design.category_ids[0]);
                    categoryName = cat ? cat.name : null;
                  }
                  // Support old format: single category_id
                  else if (design.category_id) {
                    const cat = categories.find(c => c.id === design.category_id);
                    categoryName = cat ? cat.name : null;
                  }
                  // Support old format: category name string
                  else if (design.category) {
                    categoryName = design.category;
                  }
                  
                  return categoryName ? (
                    <Badge variant="outline" className="mb-2">{categoryName}</Badge>
                  ) : null;
                })()}
                {design.tags && design.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {design.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {design.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{design.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
                {(design.price || design.demo_url || design.admin_url) && (
                  <div className="text-sm text-gray-600 space-y-1">
                    {design.price && (
                      <div>
                        <span className="font-medium">Fiyat:</span> <span className="text-purple-600 font-bold">{formatPrice(design.price)}</span>
                      </div>
                    )}
                    {design.demo_url && (
                      <div className="text-xs">
                        <span className="font-medium">Demo:</span> <a href={normalizeUrl(design.demo_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[200px]">{design.demo_url}</a>
                      </div>
                    )}
                    {design.admin_url && (
                      <div className="text-xs">
                        <span className="font-medium">Admin:</span> <a href={normalizeUrl(design.admin_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[200px]">{design.admin_url}</a>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col gap-2 mt-4">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(design)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(design.id)}
                      className="flex-1"
                    >
                      {design.is_active ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Pasif
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Aktif
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(design.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {(design.demo_url || design.admin_url) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => captureScreenshot(design, false)}
                      disabled={capturingScreenshots.has(design.id)}
                      className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                    >
                      {capturingScreenshots.has(design.id) ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Alınıyor...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-1" />
                          Ekran Görüntüsü Al
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold">
                {editingDesign ? 'Tasarım Düzenle' : 'Yeni Tasarım'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingDesign(null);
        setFormData({
          name: '',
          description: '',
          long_description: '',
          slug: '',
          category_ids: [],
          image_url: '',
          demo_url: '',
          admin_url: '',
          purchase_url: '',
          price: '',
          tags: [],
          features: [],
          is_active: true,
          meta_title: '',
          meta_description: '',
          meta_keywords: '',
          og_image: ''
        });
                  setTagInput('');
                  setFeatureInput('');
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Tasarım Adı
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-sm sm:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Uzun Açıklama
                </label>
                <textarea
                  value={formData.long_description || ''}
                  onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="6"
                  placeholder="Detaylı açıklama buraya yazılabilir..."
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Slug (URL)
                </label>
                <Input
                  value={formData.slug || ''}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Otomatik oluşturulur (boş bırakabilirsiniz)"
                  className="text-sm sm:text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boş bırakılırsa tasarım adından otomatik oluşturulur
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Kategoriler
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                  {categories.length === 0 ? (
                    <p className="text-xs sm:text-sm text-gray-500">Önce kategori oluşturun</p>
                  ) : (
                    categories.filter(cat => cat.is_active).map((category) => {
                      const isSelected = (formData.category_ids || []).includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            const currentIds = formData.category_ids || [];
                            if (isSelected) {
                              setFormData({ ...formData, category_ids: currentIds.filter(id => id !== category.id) });
                            } else {
                              setFormData({ ...formData, category_ids: [...currentIds, category.id] });
                            }
                          }}
                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-purple-600 text-white ring-2 ring-purple-500'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          {category.name}
                        </button>
                      );
                    })
                  )}
                </div>
                {formData.category_ids && formData.category_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1 break-words">
                    Seçili: {formData.category_ids.map(id => {
                      const cat = categories.find(c => c.id === id);
                      return cat ? cat.name : id;
                    }).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Görsel URL
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 text-sm sm:text-base"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openMediaLibrary}
                    className="px-3 w-full sm:w-auto"
                  >
                    <FolderOpen className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Demo URL
                </label>
                <Input
                  value={formData.demo_url}
                  onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                  placeholder="https://example.com/demo"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Admin URL
                </label>
                <Input
                  value={formData.admin_url}
                  onChange={(e) => setFormData({ ...formData, admin_url: e.target.value })}
                  placeholder="https://example.com/admin"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Satın Alım URL
                </label>
                <Input
                  value={formData.purchase_url}
                  onChange={(e) => setFormData({ ...formData, purchase_url: e.target.value })}
                  placeholder="https://example.com/purchase"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Fiyat
                </label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="₺299"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <TagIcon className="w-4 h-4 inline mr-1" />
                  Tag'ler
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                  {tags.length === 0 ? (
                    <p className="text-xs sm:text-sm text-gray-500">Önce tag oluşturun</p>
                  ) : (
                    tags.filter(tag => tag.is_active).map((tag) => {
                      // Normalize comparison - trim whitespace and compare
                      const tagName = (tag.name || '').trim();
                      const formTags = (formData.tags || []).map(t => typeof t === 'string' ? t.trim() : String(t).trim());
                      const isSelected = formTags.some(ft => ft === tagName);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleToggleTag(tag.name)}
                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                            isSelected
                              ? 'ring-2 ring-purple-500'
                              : 'hover:ring-2 hover:ring-gray-300'
                          }`}
                          style={{
                            backgroundColor: isSelected ? tag.color : '#f3f4f6',
                            color: isSelected ? 'white' : '#374151'
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })
                  )}
                </div>
                {formData.tags && formData.tags.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1 break-words">
                    Seçili: {formData.tags.join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Özellikler (Features)
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={handleFeatureInputKeyPress}
                    placeholder="Özellik ekle (örn: Responsive Tasarım)"
                    className="flex-1 text-sm sm:text-base"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddFeature}
                    className="px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.features && Array.isArray(formData.features) && formData.features.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                    {formData.features.map((feature, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-3 py-1 flex items-center gap-2 bg-purple-100 text-purple-700"
                      >
                        {feature}
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(feature)}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {(!formData.features || !Array.isArray(formData.features) || formData.features.length === 0) && (
                  <p className="text-xs text-gray-500 mt-1">
                    Henüz özellik eklenmemiş. Yukarıdaki alana yazarak özellik ekleyebilirsiniz.
                  </p>
                )}
              </div>

              {/* SEO Settings Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">SEO Ayarları</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Meta Başlık</label>
                    <Input
                      value={formData.meta_title}
                      onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      placeholder="SEO başlığı (60 karakter önerilir)"
                      maxLength={160}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {formData.meta_title.length} / 160 karakter
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Meta Açıklama</label>
                    <Textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      placeholder="SEO açıklaması (150-160 karakter ideal)"
                      rows={3}
                      maxLength={300}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {formData.meta_description.length} / 300 karakter
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Anahtar Kelimeler</label>
                    <Input
                      value={formData.meta_keywords}
                      onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                      placeholder="Virgülle ayrılmış anahtar kelimeler"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Örn: e-ticaret, tasarım, web sitesi
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Open Graph Görseli</label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.og_image}
                        onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                        placeholder="OG görsel URL (1200x630 px önerilir)"
                        className="flex-1"
                      />
                      <MediaSelector
                        value={formData.og_image}
                        onChange={(url) => setFormData({ ...formData, og_image: url })}
                        label=""
                        placeholder="Seç..."
                      />
                    </div>
                    {formData.og_image && (
                      <div className="mt-2">
                        <img
                          src={formData.og_image}
                          alt="OG preview"
                          className="w-64 h-36 object-cover rounded border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                />
                <label htmlFor="is_active" className="text-xs sm:text-sm text-gray-700">
                  Aktif
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-2 pt-4 border-t mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingDesign(null);
                    setFormData({
                      name: '',
                      description: '',
                      long_description: '',
                      slug: '',
                      category_ids: [],
                      image_url: '',
                      demo_url: '',
                      admin_url: '',
                      purchase_url: '',
                      price: '',
                      tags: [],
                      features: [],
                      is_active: true,
                      meta_title: '',
                      meta_description: '',
                      meta_keywords: '',
                      og_image: ''
                    });
                    setTagInput('');
                    setFeatureInput('');
                  }}
                  className="w-full sm:w-auto text-sm sm:text-base"
                >
                  İptal
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto text-sm sm:text-base">
                  {editingDesign ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      {isMediaModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Medya Kütüphanesi</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMediaModalOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
              {mediaFiles
                .filter(file => {
                  // Only show image files
                  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
                  const fileName = file.name.toLowerCase();
                  return imageExtensions.some(ext => fileName.endsWith(ext));
                })
                .length > 0 ? (
                  mediaFiles
                    .filter(file => {
                      // Only show image files
                      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
                      const fileName = file.name.toLowerCase();
                      return imageExtensions.some(ext => fileName.endsWith(ext));
                    })
                    .map((file) => (
                    <div
                      key={file.name}
                      className="cursor-pointer border rounded-lg p-2 hover:bg-gray-50"
                      onClick={() => handleMediaSelect(file.url)}
                    >
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-20 object-cover rounded"
                      />
                      <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full flex flex-col items-center justify-center py-8 text-gray-500">
                    <Image className="w-12 h-12 mb-2" />
                    <p>Medya kütüphanesinde resim bulunamadı</p>
                    <p className="text-sm">Lütfen önce medya kütüphanesine resim yükleyin</p>
                  </div>
                )}
            </div>

            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => setIsMediaModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <TagIcon className="w-5 h-5" />
                {editingTag ? 'Tag Düzenle' : 'Yeni Tag'}
              </h2>
              <Button variant="ghost" onClick={() => setShowTagModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Adı *</label>
                <input
                  type="text"
                  value={tagFormData.name}
                  onChange={(e) => setTagFormData({...tagFormData, name: e.target.value})}
                  placeholder="Örn: Modern, Minimalist, B2B"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea
                  value={tagFormData.description}
                  onChange={(e) => setTagFormData({...tagFormData, description: e.target.value})}
                  rows={2}
                  placeholder="Tag hakkında kısa açıklama (opsiyonel)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Renk</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({...tagFormData, color: e.target.value})}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({...tagFormData, color: e.target.value})}
                    placeholder="#EC4899"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <div 
                    className="w-10 h-10 rounded-lg border border-gray-300"
                    style={{ backgroundColor: tagFormData.color }}
                  ></div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                <input
                  type="number"
                  value={tagFormData.sort_order}
                  onChange={(e) => setTagFormData({...tagFormData, sort_order: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={tagFormData.is_active}
                    onChange={(e) => setTagFormData({...tagFormData, is_active: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Aktif</span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowTagModal(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveTag}>
                <TagIcon className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEcommerceDesigns;